import React, { useState, useEffect } from 'react';
import moment from 'moment';
import Notify from '../Notify';
import './OperadorMovil.css';

const OperadorMovil = ({ sesion }) => {
  // Manejo seguro del usuario logeado desde el reducer de sesión
  const idOperario = sesion?.IdUsuario || sesion?.IdEmpleado;
  const nombreOperario = sesion?.Nombres || sesion?.Nombre;

  const [showDisponibilidad, setShowDisponibilidad] = useState(true);
  const [isActivo, setIsActivo] = useState(false);
  const [isPausado, setIsPausado] = useState(false);
  const [despachos, setDespachos] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [loadingToggle, setLoadingToggle] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [searchTerm, setSearchTerm] = useState('');

  // Reloj local para actualización fluida del tiempo
  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);

  // Cargar estado inicial del operario y lista de despachos
  useEffect(() => {
    let active = true;
    const fetchEstadoOperario = async () => {
      try {
        const response = await fetch(`/api/despacho/operador/estado/${idOperario}`);
        const data = await response.json();
        if (active && data.ok && data.estado) {
          setIsActivo(data.estado.Estado === 'ACTIVO');
          setIsPausado(data.estado.TipoCambio === 'PAUSA');
        }
      } catch (error) {
        console.error('Error cargando estado del operario:', error);
      }
    };

    fetchEstadoOperario();

    return () => {
      active = false;
    };
  }, [idOperario]);

  // Polling cada 15s, se activa solo si NO está pausado y SÍ está activo (opcional, o solo basado en pausa)
  useEffect(() => {
    if (isPausado || !isActivo) return;

    fetchDespachos();
    const interval = setInterval(fetchDespachos, 15000);
    return () => clearInterval(interval);
  }, [isPausado, isActivo]);

  const fetchDespachos = async () => {
    try {
      const response = await fetch(`/api/despacho/pendientes?idOperario=${idOperario}`);
      const data = await response.json();
      if (data.ok) {
        const fetchTime = Date.now();
        const desp = (data.despachos || []).map(d => ({ ...d, _fetchTime: fetchTime }));
        setDespachos(desp);

        // Bloqueo: si hay uno en proceso, forzar a que se expanda
        const enProceso = desp.find(d => d.Estado === 'EN_PROCESO');
        if (enProceso) {
          setExpandedId(enProceso.IdDespacho);
        }
      }
    } catch (error) {
      console.error('Error cargando despachos pendientes:', error);
    }
  };

  const handleToggleEstado = async () => {
    setLoadingToggle(true);
    const nuevoEstado = !isActivo ? 'ACTIVO' : 'INACTIVO';
    const tipoCambio = !isActivo ? 'INICIO' : 'FIN'; // simplificado

    // Actualización local optimista
    setIsActivo(!isActivo);

    try {
      await fetch('/api/despacho/operador/evento', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          IdOperario: idOperario,
          Estado: nuevoEstado,
          TipoCambio: tipoCambio
        })
      });
    } catch (error) {
      console.error('Error actualizando estado del operario:', error);
      // Revertir si falla
      setIsActivo(isActivo);
      alert('Hubo un error de conexión al cambiar el estado.');
    } finally {
      setLoadingToggle(false);
    }
  };

  const handleTogglePausa = async () => {
    if (!isActivo) return; // No se puede pausar si está inactivo
    setLoadingToggle(true);
    const nuevoPausado = !isPausado;
    const tipoCambio = nuevoPausado ? 'PAUSA' : 'FIN_PAUSA';

    setIsPausado(nuevoPausado);

    try {
      await fetch('/api/despacho/operador/evento', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          IdOperario: idOperario,
          Estado: 'ACTIVO',
          TipoCambio: tipoCambio
        })
      });
    } catch (error) {
      console.error('Error actualizando estado de pausa:', error);
      setIsPausado(isPausado);
      alert('Hubo un error de conexión al cambiar la pausa.');
    } finally {
      setLoadingToggle(false);
    }
  };

  const toggleAccordion = (id) => {
    // Bloquear el cierre/apertura si existe algún despacho 'EN_PROCESO'
    const hayEnProceso = despachos.some(d => d.Estado === 'EN_PROCESO');
    if (hayEnProceso) {
      // Mostrar toast/alerta indicando que debe terminarlo primero
      Notify('Tienes un despacho en proceso. Termínalo antes de gestionar otro.', 'warning');
      return;
    }
    setExpandedId(expandedId === id ? null : id);
  };

  const cambiarEstado = async (idDespacho, estadoActual, nuevoEstado) => {
    const itemTarget = despachos.find(d => d.IdDespacho === idDespacho);
    const numeroComprobante = itemTarget?.NumeroComprobante || '';

    // 1. Validación: Impedir múltiples en proceso
    if (nuevoEstado === 'EN_PROCESO') {
      const hayOtroEnProceso = despachos.some(d => d.Estado === 'EN_PROCESO' && d.IdDespacho !== idDespacho);
      if (hayOtroEnProceso) {
        Notify('Ya tienes un despacho en proceso. Termínalo primero.', 'warning');
        return;
      }
    }

    try {
      // 2. Llamada a la API (se espera el resultado antes de actualizar visualmente)
      const response = await fetch('/api/despacho/estado', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          IdDespacho: idDespacho,
          IdOperario: idOperario,
          NuevoEstado: nuevoEstado
        })
      });

      const data = await response.json();

      if (!response.ok || !data.ok) {
        Notify(data.error || 'Error al actualizar el estado', 'error');
        // Refetch para asegurar estado correcto en la lista
        fetchDespachos();
        return;
      }

      // 3. Actualización Local si la respuesta fue exitosa
      if (nuevoEstado === 'DESPACHADO') {
        setDespachos(despachos.map(d =>
          d.IdDespacho === idDespacho ? { ...d, animatingOut: true } : d
        ));

        // Mostrar la notificación de éxito
        Notify(`Despacho D-${idDespacho} - ${numeroComprobante} terminado correctamente.`, 'success');

        // Esperar 500ms a que termine la animación de desvanecimiento y colapso
        setTimeout(() => {
          setDespachos(currentDespachos => {
            const item = currentDespachos.find(d => d.IdDespacho === idDespacho);
            if (item && item.animatingOut) {
              return currentDespachos.filter(d => d.IdDespacho !== idDespacho);
            }
            return currentDespachos;
          });
          setExpandedId(null); // Liberamos el acordeón
        }, 500);
      } else {
        setDespachos(despachos.map(d => {
          if (d.IdDespacho === idDespacho) {
            const result = { ...d, Estado: nuevoEstado };
            if (nuevoEstado === 'EN_PROCESO') {
              // Para el cálculo optimista, forzamos que totalSecondsProceso sea 0 en este instante
              result.SegundosEnProceso = -Math.floor((Date.now() - (d._fetchTime || Date.now())) / 1000);
            }
            return result;
          }
          return d;
        }));
        if (nuevoEstado === 'EN_PROCESO') {
          setExpandedId(idDespacho); // Bloqueamos a este
          Notify(`Despacho D-${idDespacho} - ${numeroComprobante} puesto en proceso.`, 'info');
        }
      }

      // 4. Refetch background opcional para asegurar sincronización
      fetchDespachos();
    } catch (error) {
      console.error('Error al cambiar estado:', error);
      Notify('Error de red al actualizar el estado del despacho.', 'error');
    }
  };

  const filteredDespachos = despachos.filter(item => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    const matchId = String(item.IdRegistroVenta || '').toLowerCase().includes(term);
    const matchNombre = String(item.NombreCliente || '').toLowerCase().includes(term);
    const matchAlias = String(item.Alias || '').toLowerCase().includes(term);
    const matchComprobante = String(item.NumeroComprobante || '').toLowerCase().includes(term);
    const numComprobante = parseInt(item.NumeroComprobante, 10);
    const matchComprobanteNum = !isNaN(numComprobante) && String(numComprobante).includes(term);

    return matchId || matchNombre || matchAlias || matchComprobante || matchComprobanteNum;
  });

  return (
    <div className="op-movil-container pb-4" style={{ minHeight: 'calc(100vh - 60px)' }}>

      {/* TOGGLE SWITCH DE DISPONIBILIDAD */}
      <section className="op-config-accordion py-3 px-2 my-4 mx-auto shadow-sm rounded" style={{ maxWidth: '600px' }}>
        <button
          type="button"
          className={`op-config-trigger ${showDisponibilidad ? 'open' : ''}`}
          aria-expanded={showDisponibilidad}
          aria-controls="op-config-body"
          onClick={() => setShowDisponibilidad(prev => !prev)}
        >
          <div className="op-config-trigger-copy">
            <span className="font-weight-bold text-dark d-block" style={{ fontSize: '0.95rem', lineHeight: '1.2' }}>{nombreOperario}</span>
            <small className="text-muted">Usuario de Despacho</small>
          </div>
          <div className="op-config-trigger-meta">
            <span className={`op-status-badge ${isActivo ? 'activo' : 'inactivo'} badge badge-pill px-3 py-2 font-weight-bold shadow-xs`}>
              <i className={`fas fa-circle mr-1 ${isActivo ? 'text-success' : 'text-danger'}`} style={{ fontSize: '0.65rem' }}></i>
              {isActivo ? 'ACTIVO' : 'INACTIVO'}
            </span>
            <span className="op-config-chevron" aria-hidden="true">
              <i className="fas fa-chevron-down"></i>
            </span>
          </div>
        </button>

        <div id="op-config-body" className={`op-config-body ${showDisponibilidad ? 'open' : ''}`}>
          <div className="row no-gutters">
            <div className="col-12 col-sm-6 d-flex align-items-center justify-content-between px-3 py-2">
              <span className="op-toggle-label font-weight-bold">
                <i className="fas fa-power-off text-muted mr-2"></i>Disponibilidad
              </span>
              <label className="op-switch m-0">
                <input
                  type="checkbox"
                  checked={isActivo}
                  onChange={handleToggleEstado}
                  disabled={loadingToggle}
                />
                <span className="op-slider p-0"></span>
              </label>
            </div>

            <div className={`col-12 col-sm-6 d-flex align-items-center justify-content-between px-3 py-2 ${!isActivo ? 'disabled-group' : ''}`}>
              <span className="op-toggle-label font-weight-bold">
                <i className="fas fa-pause-circle text-muted mr-2"></i>Pausar Peticiones
              </span>
              <label className="op-switch switch-pausa m-0">
                <input
                  type="checkbox"
                  checked={isPausado}
                  onChange={handleTogglePausa}
                  disabled={loadingToggle || !isActivo}
                />
                <span className="op-slider p-0"></span>
              </label>
            </div>
          </div>
        </div>
      </section>

      {isPausado && (
        <div className="text-center p-2 mb-3 text-danger font-weight-bold mx-auto rounded" style={{ backgroundColor: '#fff3cd', color: '#856404', maxWidth: '600px' }}>
          <i className="fas fa-exclamation-triangle mr-2"></i> Estás en PAUSA. No recibirás nuevos despachos hasta reanudar.
        </div>
      )}

      {/* LISTA DE DESPACHOS */}
      <section className="op-lista-section px-3 mx-auto" style={{ maxWidth: '600px' }}>
        <h3 className="op-lista-title h4 mb-3 pb-2 border-bottom">
          <i className="fas fa-clipboard-list mr-2"></i> Lista de Despachos Pendientes
        </h3>

        <div className="mb-4">
          <input
            type="text"
            className="form-control"
            placeholder="Buscar por ID, Cliente o Alias..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value.toUpperCase())}
          />
        </div>

        {filteredDespachos.length === 0 ? (
          <div className="text-center p-4 text-muted">No hay despachos que coincidan con la búsqueda.</div>
        ) : (
          filteredDespachos.map((item) => {
            const isOpen = expandedId === item.IdDespacho;
            const isPendiente = item.Estado === 'PENDIENTE';
            const isEnProceso = item.Estado === 'EN_PROCESO';

            const secondsSinceFetch = item._fetchTime ? Math.floor(Math.max(0, now - item._fetchTime) / 1000) : 0;
            const totalSeconds = (item.SegundosTranscurridos || 0) + secondsSinceFetch;

            // Calculamos segundos desde que pasó a EN_PROCESO
            const totalSecondsProceso = (item.SegundosEnProceso !== undefined && item.SegundosEnProceso !== null)
              ? item.SegundosEnProceso + secondsSinceFetch
              : 0;

            const isDespachadoHabilitado = isEnProceso && totalSecondsProceso >= 3;
            const faltanParaHabilitar = 3 - totalSecondsProceso;

            const mFormat = Math.floor(totalSeconds / 60);
            const sFormat = totalSeconds % 60;
            const timeString = `${mFormat}m ${sFormat}s`;

            return (
              <div key={item.IdDespacho} className={`op-card bg-white rounded shadow-sm mb-3 overflow-hidden ${item.animatingOut ? 'op-card-fade-out' : ''}`}>
                {/* TARJETA COMPACTA ORIGINAL */}
                <div className="op-card-header d-flex justify-content-between align-items-center p-3" onClick={() => toggleAccordion(item.IdDespacho)}>
                  <div className="op-card-info d-flex flex-column flex-md-row justify-content-between align-items-md-center flex-grow-1 mr-3">
                    <div className="d-flex flex-column mb-2 mb-md-0">
                      {/* 2. Información de la Orden (Posicionado segundo, se le añade mt-1) */}
                      <div className="op-card-order font-weight-bold d-flex align-items-center mt-1">
                        {/* <span className="mr-2">
                          <i className={isEnProceso ? "fas fa-sync fa-spin text-primary" : "far fa-clock text-warning"}></i>
                        </span> */}
                        <span className="text-dark">ORDEN - {parseInt(item.NumeroComprobante, 10)}</span>
                      </div>
                      {/* 1. Información del Cliente (Posicionado primero) */}
                      <div className="op-card-client d-flex align-items-center text-muted" style={{ fontSize: '0.9rem', flex: '1' }}>
                        <span className="mr-1">Cliente:</span> <strong className="text-dark">{item.Alias || 'VARIOS'}</strong>
                      </div>
                    </div>

                    <div className="d-flex align-items-center">
                      <div className="op-card-time px-2 py-1 mr-2 d-inline-flex align-items-center">
                        <i className="fas fa-stopwatch mr-1"></i>
                        <span style={{ minWidth: '70px', textAlign: 'center' }}>{timeString}</span>
                      </div>
                      <span className={`badge badge-pill ${isEnProceso ? 'badge-primary' : 'badge-warning'} px-2 py-1 text-white`} style={{ fontSize: '0.75rem', textTransform: 'uppercase' }}>
                        {isEnProceso ? 'En Proceso' : 'Pendiente'}
                      </span>
                    </div>
                  </div>
                  <div className={`op-chevron p-2 ${isOpen ? 'open' : ''}`}>
                    <i className="fas fa-chevron-down"></i>
                  </div>
                </div>

                {/* VISTA DETALLADA DESPLEGADA (ACORDEÓN) */}
                <div className={`op-card-body ${isOpen ? 'open' : ''} p-3`}>
                  <h4 className="op-details-title h6 font-weight-bold mb-3 text-secondary">
                    <i className="fas fa-box-open mr-1"></i> Detalle de Productos
                  </h4>

                  <ul className="op-products-list list-unstyled m-0 mb-3 bg-white border rounded p-3 shadow-xs">
                    {(item.Detalles || []).map((prod, idx) => (
                      <li key={idx} className="d-flex align-items-center pb-2 mb-2" style={{ borderBottom: idx === ((item.Detalles || []).length - 1) ? 'none' : '1px dashed #eee' }}>
                        <span className="op-product-qty font-weight-bold" style={{ color: '#008080', width: '35px', flexShrink: 0, fontSize: '1.1rem' }}>{prod.Cantidad}</span>
                        <span className="text-dark font-weight-bold" style={{ fontSize: '0.95rem', flex: 1 }}>{prod.Producto}</span>
                        {prod.PrecioVenta && (
                          <span className="text-dark">{prod.PrecioVenta.toFixed(2)}</span>
                        )}
                        {prod.Total && (
                          <span className="text-dark">{prod.Total.toFixed(2)}</span>
                        )}
                      </li>
                    ))}
                    {(!item.Detalles || item.Detalles.length === 0) && (
                      <li className="text-muted text-center py-2">No hay detalle de productos disponible.</li>
                    )}
                  </ul>
                  {/* BOTONES INTERACTIVOS SECUENCIALES */}
                  <div className="row mt-3 mb-2">
                    <div className="col-12 col-sm-6 pr-sm-2 mb-2 mb-sm-0">
                      <button
                        className={`op-btn op-btn-proceso btn btn-block font-weight-bold d-flex flex-column flex-md-row justify-content-center align-items-center py-2 py-md-3 px-2 ${isPendiente ? 'active-step' : ''}`}
                        disabled={!isPendiente}
                        onClick={() => cambiarEstado(item.IdDespacho, item.Estado, 'EN_PROCESO')}
                      >
                        <i className="fas fa-play-circle mb-1 mb-md-0 mr-md-2"></i>
                        En Proceso
                      </button>
                    </div>
                    <div className="col-12 col-sm-6 pl-sm-2">
                      <button
                        className={`op-btn op-btn-despachado btn btn-block font-weight-bold d-flex flex-column flex-md-row justify-content-center align-items-center py-2 py-md-3 px-2 ${isDespachadoHabilitado ? 'active-step' : ''}`}
                        disabled={!isDespachadoHabilitado}
                        onClick={() => cambiarEstado(item.IdDespacho, item.Estado, 'DESPACHADO')}
                      >
                        <i className="fas fa-check-circle mb-1 mb-md-0 mr-md-2"></i>
                        {isEnProceso && !isDespachadoHabilitado ? `Espera ${faltanParaHabilitar}s...` : 'Despachado'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </section>
    </div>
  );
};

export default OperadorMovil;
